import { Capacitor } from '@capacitor/core';
import { api, apiRequest } from '@/api/client';

/**
 * Push notifications no app nativo (Capacitor) via FCM unificado.
 *
 * Android e iOS geram um token FCM (o plugin cuida do mapeamento APNs→FCM no
 * iOS). Registramos o token no backend (`POST /push/register`), que manda os
 * pushes por um canal só (FCM HTTP v1). No PWA/web é no-op.
 *
 * O plugin (`@capacitor-firebase/messaging`, que arrasta o SDK `firebase`) é
 * importado dinamicamente DENTRO do guard de nativo — assim o bundle do PWA não
 * carrega o firebase (fica num chunk lazy que o web nunca pede).
 *
 * Recurso nativo real (valor de verdade + anti-Guideline 4.2 da Apple):
 * "recarga concluída" e "saldo baixo" chegam mesmo com o app fechado.
 *
 * Separação prompt × registro (onboarding de permissões, 2026-07-04):
 *  - `registerPushIfGranted()` NUNCA mostra prompt — registra silenciosamente
 *    quando a permissão já foi concedida. É o que roda em todo boot logado.
 *  - `requestPushPermissionAndRegister()` dispara o prompt nativo — chamado
 *    SÓ pela tela de onboarding (/onboarding/permissoes) e pelo Perfil.
 *    No iOS o prompt do sistema só pode ser exibido UMA vez; o priming mora
 *    nessas telas, não aqui.
 */

export type PushPermission = 'granted' | 'denied' | 'prompt';

function platform(): 'ios' | 'android' {
  return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
}

// NUNCA retornar o proxy do plugin através de `await` (o await acessa `.then`
// no proxy → "FirebaseMessaging.then() is not implemented"). Sempre
// desestruturar do módulo: `const { FirebaseMessaging } = await messagingModule()`.
async function messagingModule() {
  return import('@capacitor-firebase/messaging');
}

async function sendToken(token: string): Promise<void> {
  try {
    await api.post('/push/register', { token, platform: platform() });
  } catch {
    /* best-effort: falha de registro não atrapalha o uso do app */
  }
}

let registered = false;
// Handle do listener de rotação de token, pra remover só ele no logout.
let tokenListener: { remove: () => Promise<void> } | null = null;

/** Estado atual da permissão de notificações (pra UI de onboarding/Perfil). */
export async function pushPermissionStatus(): Promise<PushPermission> {
  if (!Capacitor.isNativePlatform()) return 'denied';
  try {
    const { FirebaseMessaging } = await messagingModule();
    const { receive } = await FirebaseMessaging.checkPermissions();
    if (receive === 'granted') return 'granted';
    if (receive === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'denied';
  }
}

/** Fluxo comum pós-permissão: listener de rotação + token inicial. */
async function registerTokenFlow(): Promise<void> {
  const { FirebaseMessaging } = await messagingModule();
  // Re-registra quando o FCM rotaciona o token (reinstalação, restore, etc.).
  // Guarda o handle pra remover SÓ este no logout — ver unregisterPush.
  tokenListener = await FirebaseMessaging.addListener('tokenReceived', (event) => {
    if (event?.token) void sendToken(event.token);
  });

  const { token } = await FirebaseMessaging.getToken();
  if (token) await sendToken(token);
}

/**
 * Registra o token FCM SE a permissão já foi concedida — sem prompt, nunca.
 * Idempotente por sessão. Chamar quando o usuário já está autenticado (o
 * endpoint exige auth). Usuário que concedeu no onboarding (ou nas Ajustes)
 * continua registrado a cada boot por aqui.
 */
export async function registerPushIfGranted(): Promise<void> {
  if (!Capacitor.isNativePlatform() || registered) return;
  registered = true;

  try {
    const { FirebaseMessaging } = await messagingModule();
    const { receive } = await FirebaseMessaging.checkPermissions();
    if (receive !== 'granted') {
      registered = false; // permite nova tentativa (ex.: liberou nas settings)
      return;
    }
    await registerTokenFlow();
  } catch {
    registered = false;
    /* sem Firebase configurado (dev) ou erro — silencioso */
  }
}

/**
 * Dispara o prompt nativo de notificações e, se concedido, registra o token.
 * Retorna o desfecho pra tela chamadora (onboarding/Perfil) reagir.
 */
export async function requestPushPermissionAndRegister(): Promise<
  'granted' | 'denied'
> {
  if (!Capacitor.isNativePlatform()) return 'denied';
  try {
    const { FirebaseMessaging } = await messagingModule();
    const perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== 'granted') return 'denied';
    if (!registered) {
      registered = true;
      await registerTokenFlow();
    }
    return 'granted';
  } catch {
    registered = false;
    return 'denied';
  }
}

/** Remove o token atual no backend (ex.: logout). Best-effort. */
export async function unregisterPush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { FirebaseMessaging } = await messagingModule();
    const { token } = await FirebaseMessaging.getToken();
    if (token) {
      await apiRequest('/push/register', {
        method: 'DELETE',
        body: { token, platform: platform() }
      });
    }
    // Remove SÓ o listener de rotação de token. `removeAllListeners()`
    // derrubaria junto o de navegação por push (pushNavigation.ts), que é
    // registrado uma vez por montagem do App e não voltaria no próximo login.
    await tokenListener?.remove();
    tokenListener = null;
  } catch {
    /* silencioso */
  } finally {
    registered = false;
  }
}
